import React, { useState, useCallback, useEffect } from "react"
import { Row, Col, Form, Button } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import useAuthAxios from "../hooks/useAuthAxios"
import Layout from "../components/layout/Layout"
import { filterEmptyFields } from "../utils/searchUtils"
import { usePersistedState } from "../hooks/usePersistedState"
import DataTable from "../components/common/DataTable"
import DateTimePicker from "../components/common/DateTimePicker"
import { formatDateForDisplay, formatDateForApi } from "../utils/dateUtils"

const GaragesSearch = () => {
  const navigate = useNavigate()
  const { get } = useAuthAxios()

  const [hasSearched, setHasSearched] = useState(false)
  const [searchCriteria, setSearchCriteria] = usePersistedState("garageSearchCriteria", {
    GarageID: "",
    Eponimia: "",
    AFM: "",
    Active: "1",
    RegistrationDate: null
  })
  const [searchResults, setSearchResults] = usePersistedState("garages-searchResults", [])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)

  useEffect(() => {
    return () => {
      const nextPath = window.location.pathname

      if (!nextPath.startsWith("/garages")) {
        // Clear React state
        setSearchCriteria({
          GarageID: "",
          Eponimia: "",
          AFM: "",
          Active: "1",
          RegistrationDate: null
        })
        setSearchResults([])

        // Clear localStorage
        localStorage.removeItem("garages")
        localStorage.removeItem("garageSearchCriteria")
        localStorage.removeItem("garages-searchResults")
      }
    }
  }, [setSearchCriteria, setSearchResults])

  const handleReset = useCallback(() => {
    setSearchCriteria({
      GarageID: "",
      Eponimia: "",
      AFM: "",
      Active: "1",
      RegistrationDate: null
    })
    setSearchResults([])
    setSearchError(null)
    setHasSearched(false)
  }, [setSearchCriteria, setSearchResults])

  const handleSearch = async e => {
    if (e) {
      e.preventDefault()
    }
    setSearching(true)
    setSearchError(null)
    setSearchResults([])
    setHasSearched(true)

    try {
      const filteredCriteria = filterEmptyFields(searchCriteria)
      filteredCriteria.RegistrationDate = formatDateForApi(filteredCriteria.RegistrationDate)
      const response = await get("/garages", { params: filteredCriteria })

      if (response.success && response.data) {
        setSearchResults(Array.isArray(response.data) ? response.data : [response.data])
      } else if (response.data?.Errors?.Problem) {
        setSearchError(response.data.Errors.Problem)
      } else {
        setSearchResults([])
      }
    } catch (error) {
      console.error("Σφάλμα αναζήτησης συνεργείων:", error)
      if (error.response?.status !== 404) {
        setSearchError("Αποτυχία αναζήτησης. Παρακαλώ προσπαθήστε ξανά.")
      }
    } finally {
      setSearching(false)
    }
  }

  const handleCriteriaChange = (field, value) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <Layout>
      <Form onSubmit={handleSearch}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Αναζήτηση Συνεργείων</h5>
        </div>
        <Row className="g-3">
          <Col lg={2} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Κωδικός Συνεργείου</Form.Label>
              <Form.Control
                type="number"
                name="GarageID"
                value={searchCriteria.GarageID}
                onChange={e => handleCriteriaChange("GarageID", e.target.value)}
                disabled={searching}
              />
            </Form.Group>
          </Col>
          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ονομασία</Form.Label>
              <Form.Control
                type="text"
                name="Eponimia"
                value={searchCriteria.Eponimia}
                onChange={e => handleCriteriaChange("Eponimia", e.target.value)}
                disabled={searching}
              />
            </Form.Group>
          </Col>
          <Col lg={2} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">ΑΦΜ</Form.Label>
              <Form.Control
                type="text"
                name="AFM"
                value={searchCriteria.AFM}
                onChange={e => handleCriteriaChange("AFM", e.target.value)}
                disabled={searching}
              />
            </Form.Group>
          </Col>
          <Col lg={2} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Κατάσταση</Form.Label>
              <Form.Select
                name="Active"
                value={searchCriteria.Active}
                onChange={e => handleCriteriaChange("Active", e.target.value)}
                disabled={searching}
                className="form-control">
                <option value="1">Ενεργά</option>
                <option value="0">Ανενεργά</option>
                <option value="">Όλα</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. Εγγραφής Συνεργείου</Form.Label>
              <DateTimePicker
                value={searchCriteria.RegistrationDate}
                onChange={value => handleCriteriaChange("RegistrationDate", value)}
                disabled={searching}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col xs={12} className="mt-4">
            <div className="d-flex justify-content-between">
              <Button type="submit" variant="primary" disabled={searching}>
                {searching ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Αναζήτηση...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2" />
                    Αναζήτηση
                  </>
                )}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={searching}>
                <i className="fas fa-undo me-2" />
                Επαναφορά
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      <hr className="my-4" />

      {/* Results Section */}
      {searching ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Φόρτωση...</span>
          </div>
        </div>
      ) : searchError ? (
        <div className="text-center py-5 text-danger">
          <i className="fas fa-exclamation-circle fa-2x mb-3 d-block" />
          <p className="mb-0">{searchError}</p>
        </div>
      ) : searchResults.length > 0 ? (
        <DataTable
          data={searchResults}
          loading={searching}
          error={searchError}
          onRowClick={item => navigate(`/garages/${item.GarageID}`, { state: { garageData: item } })}
          columns={[
            {
              header: "Κωδικός",
              field: "GarageID",
              type: "string"
            },
            {
              header: "Επωνυμία",
              field: "Eponimia",
              type: "string"
            },
            {
              header: "ΑΦΜ",
              field: "AFM",
              type: "string"
            },
            {
              header: "Πόλη",
              field: "AddressCity",
              type: "string"
            },
            {
              header: "Κατάσταση",
              field: "Active",
              type: "boolean",
              render: item => (
                <span className={`badge bg-${item.Active ? "success" : "danger"}`}>
                  {item.Active ? "Ενεργό" : "Ανενεργό"}
                </span>
              )
            },
            {
              header: "Ημ. Εγγραφής Συνεργείου",
              field: "RegistrationDate",
              type: "date",
              render: item => formatDateForDisplay(item.RegistrationDate)
            }
          ]}
        />
      ) : hasSearched ? (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-search fa-2x mb-3 d-block" />
          <p className="mb-0">Δεν βρέθηκαν συνεργεία που να αντιστοιχούν στα κριτήρια αναζήτησης.</p>
        </div>
      ) : null}
    </Layout>
  )
}

export default GaragesSearch
